import {ICrp, ICrpBpListItem} from 'api/business-partner';
import {useCreateCrp, useUpdateCrp} from 'api/crp';
import {WithFormError} from 'components/ui/Form/Form/Form';
import {CrpVariants} from 'constants/businessPartnerAttributes';
import {CRP_BP_LIST_CACHE} from 'constants/cache';
import useModal from 'components/ui/ModalContainer/useModal';
import {cleanEmpty, extract} from 'helpers/common';
import {handleTypedFormError} from 'helpers/handleFormError';
import {useMemo} from 'react';
import {UseFormMethods} from 'react-hook-form';
import {useCrpStore} from 'components/pages/OnboardingPage/components/store/crpStore';
import {CrpSelectedRoles, ICrpFormValues} from '../CrpForm';
import {useQueryClient} from '@tanstack/react-query';

interface ICrpUpdateValues {
    '@id': string;
    lastName: string;
    firstName: string;
    email: string | null;
    phoneNumber?: string;
    street?: string;
    city?: string;
    zip?: string;
    country?: string;
    gender?: string;
    nationality?: string;
    dateOfBirth?: string;
    role: string;
    businessPartner: string;
}

const extractCrpUpdateValues = (confidential: boolean) => {
    if (confidential) {
        return extract<
            Pick<ICrpUpdateValues, 'firstName' | 'lastName' | 'email'>
        >({
            lastName: true,
            firstName: true,
            email: true,
        });
    }
    return extract<
        Omit<ICrpUpdateValues, 'businessPartner' | '@id' | 'phoneNumber'>
    >({
        lastName: true,
        firstName: true,
        street: true,
        city: true,
        zip: true,
        country: true,
        email: true,
        gender: true,
        nationality: true,
        dateOfBirth: true,
        role: true,
    });
};

interface IUseCrpTable {
    crpList: ICrpBpListItem[];
    currentStep: number;
    isIndividual?: boolean;
}

const useCrpTable = ({crpList}: IUseCrpTable) => {
    const {closeModal} = useModal();
    const {mutate: createCrp, isPending: isCreateCrpLoading} = useCreateCrp();
    const {mutate: updateCrp, isPending: isUpdateCrpLoading} = useUpdateCrp();
    const setCrpLoading = useCrpStore(state => state.setIsLoading);
    const queryClient = useQueryClient();

    const handleCrpEdit = (
        values: ICrpFormValues,
        selectedRoles: CrpSelectedRoles,
        crp?: ICrpBpListItem,
        setError?: UseFormMethods['setError'],
    ) => {
        let directOwner: boolean | undefined;
        if (values.directOwner === 'null') {
            directOwner = undefined;
        } else {
            directOwner = values.directOwner
                ? values.directOwner === 'yes'
                : (crp?.directOwner ?? undefined);
        }

        const crpData: Partial<ICrpBpListItem> &
            Partial<ICrp> & {loggedInUser: boolean} = {
            ...cleanEmpty(
                extractCrpUpdateValues(!!crp?.confidential)({
                    ...crp,
                    ...crp?.crp,
                    ...values,
                }),
                [''],
            ),
            phoneNumber: values.phoneNumber || undefined,
            '@id': values['@id'],
            role: values.role ?? crp?.role,
            directOwner,
            collectiveSigningRight: values.collectiveSigningRight
                ? values.collectiveSigningRight === 'collective'
                : crp?.collectiveSigningRight,
            ownerPercentage:
                values.ownerPercentage || crp?.ownerPercentage || '0',
            highestEducation:
                values.highestEducation || crp?.crp?.highestEducation,
            professionalPosition:
                values.professionalPosition || crp?.professionalPosition,
            isOwner: selectedRoles.includes(CrpVariants.OWNER),
            hasSigningRights: selectedRoles.includes(CrpVariants.SIGNER),
            contractSigner: selectedRoles.includes(CrpVariants.CONTRACT_SIGNER),
            admin: selectedRoles.includes(CrpVariants.ADMIN),
            loggedInUser: selectedRoles.includes('currentUser'),
        };

        setCrpLoading(true);
        return crpData['@id']
            ? updateCrp(
                  {...crpData, '@id': crpData['@id']!},
                  {
                      onSuccess: () => {
                          setCrpLoading(false);
                          queryClient.invalidateQueries({
                              queryKey: [CRP_BP_LIST_CACHE],
                          });
                          closeModal();
                      },
                      onError: (requestErrors: any) => {
                          setCrpLoading(false);
                          if (setError) {
                              handleTypedFormError(
                                  requestErrors,
                                  setError,
                                  values as WithFormError<ICrpFormValues>,
                              );
                          }
                      },
                  },
              )
            : createCrp(crpData, {
                  onSuccess: () => {
                      setCrpLoading(false);
                      queryClient.invalidateQueries({
                          queryKey: [CRP_BP_LIST_CACHE],
                      });
                      closeModal();
                  },
                  onError: (requestErrors: any) => {
                      setCrpLoading(false);
                      if (setError) {
                          handleTypedFormError(
                              requestErrors,
                              setError,
                              values as WithFormError<ICrpFormValues>,
                          );
                      }
                  },
              });
    };

    const maxSignersReached = useMemo(() => {
        return crpList?.filter(crp => crp.contractSigner)?.length === 2;
    }, [crpList]);

    return {
        isLoading: isCreateCrpLoading || isUpdateCrpLoading,
        handleCrpEdit,
        maxSignersReached,
    };
};

export default useCrpTable;
